import { HeroCarousel } from "./HeroCarousel";
import { BuildingIcon, ArrowRightIcon } from "./icons";
import { GlassCard, Pill } from "./ui";
import { fmtDate, fmtWeekday, fmtTime, serviceTypeLabel, isLikelyBrokenUploadUrl } from "./utils";

const HeroSkeleton = () => (
  <section className="pt-20 pb-8 sm:pt-24 sm:pb-10" aria-busy="true" aria-label="A carregar a página pública">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-8 items-center animate-pulse">
      <div className="space-y-5">
        <div className="flex gap-2"><div className="h-7 w-16 rounded-full bg-slate-200 dark:bg-slate-700" /><div className="h-7 w-16 rounded-full bg-slate-200 dark:bg-slate-700" /><div className="h-7 w-16 rounded-full bg-slate-200 dark:bg-slate-700" /></div>
        <div className="h-14 max-w-xl rounded-2xl bg-slate-200 dark:bg-slate-700" />
        <div className="h-5 max-w-md rounded bg-slate-200 dark:bg-slate-700" />
        <div className="h-16 max-w-lg rounded bg-slate-200 dark:bg-slate-700" />
        <div className="flex gap-3"><div className="h-12 w-44 rounded-full bg-slate-300" /><div className="h-12 w-36 rounded-full bg-slate-200 dark:bg-slate-700" /></div>
        <div className="grid sm:grid-cols-3 gap-3"><div className="h-28 rounded-2xl bg-white dark:bg-slate-900/70" /><div className="h-28 rounded-2xl bg-white dark:bg-slate-900/70" /><div className="h-28 rounded-2xl bg-white dark:bg-slate-900/70" /></div>
      </div>
      <div className="h-[300px] sm:h-[360px] rounded-3xl bg-slate-200 dark:bg-slate-700" />
    </div>
  </section>
);

export const Hero = ({ hero, nextService, whatsapp, mapUrl, onScrollCultos, ctas = {}, loading = false }) => {
  if (loading) return <HeroSkeleton />;

  return (
  <section className="public-hero-section pt-20 pb-8 sm:pt-24 sm:pb-10">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-8 items-center">
      <div className="space-y-5">
        <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-indigo-700 dark:text-indigo-300">
          <span className="h-2 w-2 rounded-full bg-indigo-600" aria-hidden="true" />
          {hero?.tagline || hero?.brandName}
        </div>
        <div className="flex flex-wrap gap-2">
          <Pill>Louvor</Pill>
          <Pill>Oração</Pill>
          <Pill>Palavra</Pill>
        </div>

        <div className="space-y-2">
          <h1 className="text-3xl sm:text-5xl font-extrabold text-slate-900 dark:text-slate-100 leading-tight tracking-tight">
            {hero?.title || "Firmar, Expandir e Discipular"}
          </h1>
          <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl">
            {hero?.subtitle || "Louvor, oração e Palavra para toda a família."}
          </p>
          {(hero?.verse || hero?.verseRef) ? (
            <blockquote className="mt-3 border-l-4 border-indigo-500 pl-4 text-slate-700 dark:text-slate-300">
              {hero.verse ? (
                <p className="italic text-base sm:text-lg leading-relaxed">“{hero.verse}”</p>
              ) : null}
              {hero.verseRef ? (
                <cite className={`block text-sm font-semibold text-indigo-700 dark:text-indigo-300 not-italic ${hero.verse ? "mt-1" : ""}`}>
                  {hero.verseRef}
                </cite>
              ) : null}
            </blockquote>
          ) : null}
        </div>

        <div className="flex flex-wrap gap-2.5">
          <button
            type="button"
            onClick={onScrollCultos}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-slate-950 text-white font-semibold shadow-[0_12px_24px_rgba(15,23,42,0.18)] hover:bg-indigo-700 transition"
          >
            Ver próximo culto <ArrowRightIcon />
          </button>
          <a
            href={mapUrl || "#localizacao"}
            target={mapUrl ? "_blank" : undefined}
            rel={mapUrl ? "noreferrer" : undefined}
            className="px-5 py-3 rounded-full border border-white dark:border-slate-900/70 bg-white dark:bg-slate-900/60 backdrop-blur text-slate-800 dark:text-slate-200 font-semibold shadow-sm hover:bg-white dark:hover:bg-slate-900/75 transition"
          >
            Ver localização
          </a>
          {whatsapp ? (
            <a
              href={whatsapp}
              target="_blank"
              rel="noreferrer"
              className="px-5 py-3 rounded-full border border-white dark:border-slate-900/70 bg-white dark:bg-slate-900/60 backdrop-blur text-slate-800 dark:text-slate-200 font-semibold shadow-sm hover:bg-white dark:hover:bg-slate-900/75 transition"
            >
              WhatsApp
            </a>
          ) : null}
          {ctas.donateUrl ? (
            <a
              href={ctas.donateUrl}
              target="_blank"
              rel="noreferrer"
              className="px-5 py-3 rounded-full border border-indigo-200 dark:border-indigo-800/50 bg-indigo-50 dark:bg-indigo-950/40 text-indigo-800 dark:text-indigo-200 font-semibold shadow-sm hover:bg-indigo-100 dark:hover:bg-indigo-900/40 transition"
            >
              Doações
            </a>
          ) : null}
          {ctas.streamUrl ? (
            <a
              href={ctas.streamUrl}
              target="_blank"
              rel="noreferrer"
              className="px-5 py-3 rounded-full border border-indigo-200 dark:border-indigo-800/50 bg-indigo-50 dark:bg-indigo-950/40 text-indigo-800 dark:text-indigo-200 font-semibold shadow-sm hover:bg-indigo-100 dark:hover:bg-indigo-900/40 transition"
            >
              Assistir online
            </a>
          ) : null}
        </div>

        <div className="grid sm:grid-cols-3 gap-3 pt-1">
          <GlassCard className="p-3.5">
            <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">Próximo culto</div>
            {nextService ? (
              <div className="space-y-1">
                <div className="font-semibold text-slate-900 dark:text-slate-100 capitalize">
                  {fmtWeekday(nextService.date)} • {fmtDate(nextService.date)}
                </div>
                <div className="text-sm text-slate-600 dark:text-slate-400">{fmtTime(nextService.time)} — {serviceTypeLabel(nextService)}</div>
                {nextService.theme && <div className="text-xs text-slate-500 dark:text-slate-400">{nextService.theme}</div>}
              </div>
            ) : (
              <div className="text-sm text-slate-600 dark:text-slate-400">Em breve publicaremos os próximos cultos.</div>
            )}
          </GlassCard>

          <GlassCard className="p-3.5">
            <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">Endereço</div>
            <div className="font-semibold text-slate-900 dark:text-slate-100 text-sm leading-snug">
              {hero?.address || "Endereço a confirmar"}
            </div>
            <a href={mapUrl || "#localizacao"} target={mapUrl ? "_blank" : undefined} rel={mapUrl ? "noreferrer" : undefined} className="text-sm text-indigo-600 dark:text-indigo-300 hover:text-indigo-500 dark:hover:text-indigo-300">
              Abrir no Google Maps
            </a>
          </GlassCard>

          <GlassCard className="p-3.5">
            <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">WhatsApp</div>
            <div className="font-semibold text-slate-900 dark:text-slate-100">{hero?.whatsappDisplay || hero?.phone || "+258 82 631 9370"}</div>
            <a href={whatsapp} target="_blank" rel="noreferrer" className="text-sm text-indigo-600 dark:text-indigo-300 hover:text-indigo-500 dark:hover:text-indigo-300">
              Falar no WhatsApp
            </a>
          </GlassCard>
        </div>
      </div>

      <div className="relative">
        <div className="rounded-3xl border border-white dark:border-slate-900/60 bg-white dark:bg-slate-900/60 backdrop-blur-xl shadow-xl overflow-hidden">
          {[hero?.image, ...(hero?.slides || [])].filter(Boolean).length ? (
            <HeroCarousel
              images={[
                ...[hero?.image, ...(hero?.slides || [])].filter(
                  (u) => u && !isLikelyBrokenUploadUrl(u)
                ),
              ].filter(Boolean)}
              alt={hero?.title || "Imagem da igreja"}
              intervalMs={3200}
              fallback={
                <div className="h-[300px] sm:h-[360px] bg-gradient-to-br from-indigo-200 dark:from-indigo-800/40 via-sky-200 dark:via-sky-800/40 to-white dark:to-slate-900 flex flex-col items-center justify-center text-indigo-700 dark:text-indigo-300 font-semibold">
                  <BuildingIcon />
                  <span className="mt-2">Imagem da igreja</span>
                  <span className="mt-1 text-xs text-slate-600 dark:text-slate-400">Sugestão: use uma foto real do templo/culto</span>
                </div>
              }
            />
          ) : (
            <div className="h-[300px] sm:h-[360px] bg-gradient-to-br from-indigo-200 dark:from-indigo-800/40 via-sky-200 dark:via-sky-800/40 to-white dark:to-slate-900 flex flex-col items-center justify-center text-indigo-700 dark:text-indigo-300 font-semibold">
              <BuildingIcon />
              <span className="mt-2">Imagem da igreja</span>
              <span className="mt-1 text-xs text-slate-600 dark:text-slate-400">Sugestão: usar uma foto real do templo/culto</span>
            </div>
          )}

          <div className="p-4 sm:p-5 bg-white dark:bg-slate-900/40 border-t border-white dark:border-slate-900/60">
            <div className="text-xs text-slate-600 dark:text-slate-400">{hero?.brandName}</div>
            <div className="mt-1 text-base font-semibold text-slate-900 dark:text-slate-100">
              {hero?.caption || "Um lugar para crescer na fé em família."}
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
  );
};
